import type { TownMatchesResponse } from "./api";
import type { User } from "@/types";
import { normalizeSearchQueryForSubmission } from "./search-normalization";

export type RecommendationSource =
  | "recent_search"
  | "saved_search"
  | "profile"
  | "browser_location"
  | "approximate_region"
  | "fallback";

export interface HomepageSearchRecommendation {
  label: string;
  query: string;
  reason: string;
  source: RecommendationSource;
}

export interface MarketRecommendationConfig {
  label: string;
  description: string;
  towns: string[];
  starterSearches: string[];
}

export type MarketKey = "morris" | "hudson" | "bergen" | "essexUnion" | "westernNj";

export type AutocompleteCategory = "town" | "zip" | "nearby" | "suggested" | "school" | "custom" | "didyoumean";

export interface HomepageAutocompleteSuggestion {
  label: string;
  query: string;
  detail: string;
  category: AutocompleteCategory;
  source: RecommendationSource;
}

export interface SearchAssistMarket {
  displayName: string;
  state: string;
  county: string;
  zipCodes: string[];
  nearbyMarkets: string[];
  suggestedSearches: string[];
  tags?: string[];
  schoolSuggestions?: string[];
}

export interface AvailableSearchMarket {
  city: string;
  state: string;
}

export const MARKET_RECOMMENDATIONS: Record<MarketKey, MarketRecommendationConfig> = {
  morris: {
    label: "Morris County",
    description: "Suburban towns with train access, larger lots, and move-up inventory.",
    towns: ["Morristown", "Madison", "Chatham", "Mendham", "Florham Park", "Morris Plains", "Parsippany"],
    starterSearches: [
      "Morristown 3+ beds under $900K",
      "Madison near train under $1.1M",
      "Chatham homes with yard",
      "Mendham privacy and larger lot",
    ],
  },
  hudson: {
    label: "Hudson Waterfront",
    description: "Denser inventory with condos, commute tradeoffs, parking, and building amenities.",
    towns: ["Hoboken", "Jersey City", "Weehawken", "Edgewater", "Union City", "Fort Lee"],
    starterSearches: [
      "Hoboken under $900K",
      "Weehawken near ferry",
      "Jersey City 2 beds with parking",
      "Edgewater waterfront views",
    ],
  },
  bergen: {
    label: "Bergen County",
    description: "Train towns, school-driven searches, and high-demand suburbs where street, commute, and condition change the fit quickly.",
    towns: ["Ridgewood", "Glen Rock", "Upper Saddle River", "Tenafly", "Wyckoff", "Ho-Ho-Kus", "Paramus", "Englewood", "Hackensack"],
    starterSearches: [
      "Ridgewood near train",
      "Glen Rock 3+ beds",
      "Upper Saddle River larger lots",
      "Tenafly top schools",
    ],
  },
  essexUnion: {
    label: "Essex & Union",
    description: "Commuter-friendly towns where school district, street feel, and condition matter.",
    towns: ["Maplewood", "Millburn", "Summit", "Westfield", "Montclair", "West Orange", "Livingston"],
    starterSearches: [
      "Maplewood not on a corner lot",
      "Millburn under $1.3M",
      "Summit near train",
      "Westfield 4 beds with yard",
    ],
  },
  westernNj: {
    label: "Western North Jersey",
    description: "Sussex, Warren, and Watchung-area towns with more land, lake communities, and lower-density tradeoffs.",
    towns: ["Sparta", "Newton", "Sussex", "Vernon", "Hopatcong", "Hackettstown", "Blairstown", "Phillipsburg", "Warren", "Watchung"],
    starterSearches: [
      "Sparta 3+ beds with land",
      "Hackettstown under $700K",
      "Warren good school district",
      "Watchung privacy and larger lot",
    ],
  },
};

export const SEARCH_ASSIST_MARKETS: SearchAssistMarket[] = [
  {
    displayName: "Morristown",
    state: "NJ",
    county: "Morris County",
    zipCodes: ["07960"],
    nearbyMarkets: ["Madison", "Morris Township", "Chatham", "Mendham", "Florham Park"],
    suggestedSearches: ["Morristown homes", "Morristown condos", "Morristown homes near train", "Morristown 3+ beds under $900K"],
    tags: ["train", "downtown", "condos", "single-family"],
  },
  {
    displayName: "Morris Township",
    state: "NJ",
    county: "Morris County",
    zipCodes: ["07960"],
    nearbyMarkets: ["Morristown", "Madison", "Mendham", "Florham Park", "Morris Plains"],
    suggestedSearches: ["Morris Township homes", "Morris Township 3+ beds", "Morris Township larger lots", "Morris Township under $1M"],
    tags: ["space", "schools", "single-family"],
  },
  {
    displayName: "Madison",
    state: "NJ",
    county: "Morris County",
    zipCodes: ["07940"],
    nearbyMarkets: ["Chatham", "Morristown", "Florham Park", "Morris Township", "Convent Station"],
    suggestedSearches: ["Madison homes", "Madison near train under $1.1M", "Madison move-in ready", "Madison 3+ beds"],
    tags: ["train", "schools", "downtown"],
  },
  {
    displayName: "Chatham",
    state: "NJ",
    county: "Morris County",
    zipCodes: ["07928"],
    nearbyMarkets: ["Madison", "Summit", "Millburn", "Florham Park", "Morristown"],
    suggestedSearches: ["Chatham homes", "Chatham homes with yard", "Chatham near train", "Chatham under $1.2M"],
    tags: ["train", "yard", "schools"],
  },
  {
    displayName: "Mendham",
    state: "NJ",
    county: "Morris County",
    zipCodes: ["07945"],
    nearbyMarkets: ["Mendham Township", "Chester", "Randolph", "Bernardsville", "Morristown"],
    suggestedSearches: ["Mendham homes", "Mendham homes with 4+ beds", "Mendham homes with larger lots", "Mendham homes under $1.5M", "Mendham privacy and larger lot"],
    schoolSuggestions: ["Mendham Township Board of Education School District", "Mendham Twp Middle School"],
    tags: ["privacy", "larger lots", "schools", "luxury"],
  },
  {
    displayName: "Mendham Township",
    state: "NJ",
    county: "Morris County",
    zipCodes: ["07945"],
    nearbyMarkets: ["Mendham", "Chester", "Randolph", "Bernardsville", "Morristown"],
    suggestedSearches: ["Mendham Township homes", "Mendham Township larger lots", "Mendham Township 4+ beds", "Mendham Township privacy"],
    schoolSuggestions: ["Mendham Township Board of Education School District", "Mendham Twp Middle School"],
    tags: ["privacy", "larger lots", "schools"],
  },
  {
    displayName: "Chester",
    state: "NJ",
    county: "Morris County",
    zipCodes: ["07930"],
    nearbyMarkets: ["Mendham", "Randolph", "Long Valley", "Bernardsville"],
    suggestedSearches: ["Chester homes", "Chester homes with acreage", "Chester 4+ beds", "Chester privacy and larger lot"],
    tags: ["acreage", "privacy", "larger lots"],
  },
  {
    displayName: "Randolph",
    state: "NJ",
    county: "Morris County",
    zipCodes: ["07869"],
    nearbyMarkets: ["Morristown", "Mendham", "Chester", "Denville", "Roxbury"],
    suggestedSearches: ["Randolph homes", "Randolph 4+ beds", "Randolph homes with yard", "Randolph under $900K"],
    tags: ["schools", "yard", "single-family"],
  },
  {
    displayName: "Bernardsville",
    state: "NJ",
    county: "Somerset County",
    zipCodes: ["07924"],
    nearbyMarkets: ["Basking Ridge", "Mendham", "Chester", "Far Hills"],
    suggestedSearches: ["Bernardsville homes", "Bernardsville near train", "Bernardsville larger lots", "Bernardsville privacy"],
    tags: ["train", "privacy", "luxury"],
  },
  {
    displayName: "Basking Ridge",
    state: "NJ",
    county: "Somerset County",
    zipCodes: ["07920"],
    nearbyMarkets: ["Bernardsville", "Warren", "Bedminster", "Bridgewater"],
    suggestedSearches: ["Basking Ridge homes", "Basking Ridge 4+ beds", "Basking Ridge good schools", "Basking Ridge with yard"],
    tags: ["schools", "yard", "single-family"],
  },
  {
    displayName: "Summit",
    state: "NJ",
    county: "Union County",
    zipCodes: ["07901"],
    nearbyMarkets: ["Chatham", "Millburn", "New Providence", "Westfield"],
    suggestedSearches: ["Summit homes", "Summit near train", "Summit 4 beds with yard", "Summit under $1.5M"],
    tags: ["train", "schools", "commute"],
  },
  {
    displayName: "Millburn",
    state: "NJ",
    county: "Essex County",
    zipCodes: ["07041"],
    nearbyMarkets: ["Maplewood", "Summit", "Livingston"],
    suggestedSearches: ["Millburn homes", "Millburn under $1.3M", "Millburn near train", "Millburn good schools"],
    tags: ["schools", "train", "commute"],
  },
  {
    displayName: "Florham Park",
    state: "NJ",
    county: "Morris County",
    zipCodes: ["07932"],
    nearbyMarkets: ["Madison", "Chatham", "Morristown", "Morris Township", "Livingston"],
    suggestedSearches: ["Florham Park homes", "Florham Park 3+ beds", "Florham Park under $1M", "Florham Park with yard"],
    tags: ["yard", "single-family", "commute"],
  },
  {
    displayName: "Neptune Township",
    state: "NJ",
    county: "Monmouth County",
    zipCodes: ["07753", "07754"],
    nearbyMarkets: ["Neptune City", "Asbury Park", "Ocean Grove", "Bradley Beach", "Wall Township"],
    suggestedSearches: ["Neptune Township homes", "Neptune Township condos", "Neptune Township 3+ beds", "Neptune Township near shore"],
    tags: ["shore", "single-family", "condos"],
  },
  {
    displayName: "Neptune City",
    state: "NJ",
    county: "Monmouth County",
    zipCodes: ["07753"],
    nearbyMarkets: ["Neptune Township", "Asbury Park", "Ocean Grove", "Bradley Beach"],
    suggestedSearches: ["Neptune City homes", "Neptune City condos", "Neptune City near shore"],
    tags: ["shore", "condos"],
  },
];

export interface SearchContextItem {
  query?: string | null;
  criteria?: Record<string, unknown> | null;
}

export interface BrowserLocation {
  latitude: number;
  longitude: number;
}

interface RecommendationInput {
  user?: Pick<User, "id"> | null;
  recentSearches?: SearchContextItem[];
  savedSearches?: SearchContextItem[];
  favorites?: Array<{ city?: string | null }>;
  browserLocation?: BrowserLocation | null;
  approximateRegion?: string | null;
  marketConfig?: Record<string, MarketRecommendationConfig>;
  availableMarkets?: AvailableSearchMarket[];
}

const DEFAULT_ORDER: MarketKey[] = ["morris", "essexUnion", "bergen", "hudson", "westernNj"];

function normalize(value: unknown) {
  return String(value || "").trim().toLowerCase();
}

function normalizeAutocomplete(value: unknown) {
  return normalize(value)
    .replace(/\bnew jersey\b/g, "nj")
    .replace(/\bnew york\b/g, "ny")
    .replace(/\bconnecticut\b/g, "ct")
    .replace(/\bpennsylvania\b/g, "pa")
    .replace(/[.,]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function normalizeState(value: unknown) {
  return String(value || "").trim().toUpperCase();
}

function toTitleTown(value: string) {
  return value.split(/\s+/).map(part => part ? part[0].toUpperCase() + part.slice(1).toLowerCase() : part).join(" ");
}

function formatBudget(value: unknown) {
  const num = typeof value === "number" ? value : Number(value || 0);
  if (!num) return "";
  if (num >= 1_000_000) return `$${Number((num / 1_000_000).toFixed(num % 1_000_000 === 0 ? 0 : 1))}M`;
  return `$${Math.round(num / 1000)}K`;
}

export function cityFromContext(item?: SearchContextItem | null, marketConfig = MARKET_RECOMMENDATIONS): string | null {
  const criteria = item?.criteria || {};
  const directCity = criteria.city;
  if (typeof directCity === "string" && directCity.trim()) return directCity.trim();
  const cities = criteria.cities;
  if (Array.isArray(cities) && typeof cities[0] === "string") return cities[0];
  const query = normalize(item?.query);
  if (!query) return null;
  const towns = Object.values(marketConfig).flatMap(market => market.towns);
  const match = towns.find(town => query.includes(town.toLowerCase()));
  return match || null;
}

export function searchAssistMarketForTown(town: string | null | undefined) {
  const normalizedTown = normalize(town);
  if (!normalizedTown) return null;
  return SEARCH_ASSIST_MARKETS.find(market => normalize(market.displayName) === normalizedTown) || null;
}

export function getMarketKeyForTown(town: string | null | undefined, marketConfig = MARKET_RECOMMENDATIONS): string | null {
  const normalizedTown = normalize(town);
  if (!normalizedTown) return null;
  const entry = Object.entries(marketConfig).find(([, market]) =>
    market.towns.some(candidate => normalize(candidate) === normalizedTown)
  );
  return entry?.[0] || null;
}

export function getMarketKeyForCoordinates(location: BrowserLocation | null | undefined): MarketKey | null {
  if (!location) return null;
  const { latitude, longitude } = location;
  if (latitude >= 40.68 && latitude <= 40.86 && longitude >= -74.08 && longitude <= -73.92) return "hudson";
  if (latitude >= 40.86 && latitude <= 41.12 && longitude >= -74.22 && longitude <= -73.90) return "bergen";
  if (latitude >= 40.63 && latitude <= 40.86 && longitude >= -74.42 && longitude <= -74.16) return "essexUnion";
  if (latitude >= 40.74 && latitude <= 41.05 && longitude >= -74.68 && longitude <= -74.28) return "morris";
  if (latitude >= 40.78 && latitude <= 41.38 && longitude >= -75.20 && longitude <= -74.55) return "westernNj";
  return null;
}

function getMarketKeyForApproximateRegion(region: string | null | undefined): MarketKey | null {
  const value = normalize(region);
  if (!value) return null;
  if (/hudson|hoboken|jersey city|weehawken|waterfront/.test(value)) return "hudson";
  if (/bergen|ridgewood|glen rock|tenafly/.test(value)) return "bergen";
  if (/essex|union|maplewood|summit|westfield|millburn/.test(value)) return "essexUnion";
  if (/morris|morristown|madison|chatham/.test(value)) return "morris";
  if (/sussex|warren|western/.test(value)) return "westernNj";
  if (/north jersey|new jersey|nj/.test(value)) return "morris";
  return null;
}

function starterToRecommendation(search: string, source: RecommendationSource, reason: string): HomepageSearchRecommendation {
  return { label: search, query: normalizeSearchQueryForSubmission(search), source, reason };
}

function recommendationForRecentTown(item: SearchContextItem, town: string, market: MarketRecommendationConfig): HomepageSearchRecommendation[] {
  const criteria = item.criteria || {};
  const beds = typeof criteria.min_beds === "number" ? criteria.min_beds : Number(criteria.min_beds || 0);
  const budget = formatBudget(criteria.max_price);
  const anchorMarket = searchAssistMarketForTown(town);
  const first = [
    town,
    beds ? `${beds}+ beds` : market.label === "Hudson Waterfront" ? "2 beds" : "homes",
    budget ? `under ${budget}` : "",
  ].filter(Boolean).join(" ");
  const nearby = anchorMarket?.nearbyMarkets.map(nearbyTown => `${nearbyTown} homes`) || [];
  const localSuggestions = anchorMarket?.suggestedSearches.filter(search => !normalize(search).startsWith(normalize(town))) || [];
  const related = [...nearby, ...localSuggestions, ...market.starterSearches.filter(search => !normalize(search).startsWith(normalize(town)))];
  return Array.from(new Set([...related.slice(0, 3), first]))
    .slice(0, 4)
    .map(search => starterToRecommendation(search, "recent_search", `Based on your recent ${town} search`));
}

interface AutocompleteInput extends RecommendationInput {
  query: string;
  // The backend's matches for this query (GET /listings/town-matches): exact and
  // prefix towns from the whole synced list, and a near-miss the typed text
  // resolves to. Absent until they arrive, and ignored if they are for older text.
  townMatches?: TownMatchesResponse | null;
}

function dedupeAutocomplete(items: Array<HomepageAutocompleteSuggestion & { score: number }>) {
  const seen = new Set<string>();
  return items
    .sort((a, b) => b.score - a.score || a.label.localeCompare(b.label))
    .filter(item => {
      const key = normalize(item.query);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, 9)
    .map(({ score: _score, ...item }) => item);
}

function suggestionSource(input: RecommendationInput): RecommendationSource {
  if (input.user && input.recentSearches?.length) return "recent_search";
  if (input.user && input.savedSearches?.length) return "saved_search";
  if (input.browserLocation) return "browser_location";
  if (input.approximateRegion) return "approximate_region";
  return "fallback";
}

export function getHomepageAutocompleteSuggestions(input: AutocompleteInput): HomepageAutocompleteSuggestion[] {
  const rawQuery = input.query.trim().replace(/\s+/g, " ");
  const query = normalizeAutocomplete(rawQuery);
  if (query.length < 2) return [];

  const source = suggestionSource(input);
  const recentTown = cityFromContext(input.recentSearches?.[0]);
  const savedTown = cityFromContext(input.savedSearches?.[0]);
  const anchorTown = recentTown || savedTown;
  const anchorMarket = searchAssistMarketForTown(anchorTown);
  const matchedMarkets = SEARCH_ASSIST_MARKETS.filter(market => {
    const name = normalizeAutocomplete(market.displayName);
    const state = normalizeState(market.state);
    const cityState = normalizeAutocomplete(`${market.displayName} ${state}`);
    return name.includes(query) || cityState.includes(query) || market.zipCodes.some(zip => zip.startsWith(query));
  });
  const availableMarketMatches = (input.availableMarkets || []).filter(market => {
    const city = market.city?.trim();
    const state = normalizeState(market.state);
    if (!city || !state) return false;
    const name = normalizeAutocomplete(city);
    const cityState = normalizeAutocomplete(`${city} ${state}`);
    return name.includes(query) || cityState.includes(query);
  });
  const anchorNearbyMarkets = anchorMarket
    ? SEARCH_ASSIST_MARKETS.filter(market => anchorMarket.nearbyMarkets.some(nearby => normalizeAutocomplete(nearby) === normalizeAutocomplete(market.displayName)))
    : [];
  const nearbyMatches = anchorNearbyMarkets.filter(market => normalizeAutocomplete(market.displayName).includes(query));
  const markets = [...matchedMarkets, ...nearbyMatches];
  const suggestions: Array<HomepageAutocompleteSuggestion & { score: number }> = [];

  const add = (item: HomepageAutocompleteSuggestion, score: number) => {
    suggestions.push({ ...item, score });
  };

  for (const market of markets) {
    const name = normalizeAutocomplete(market.displayName);
    const state = normalizeState(market.state);
    const cityState = normalizeAutocomplete(`${market.displayName} ${state}`);
    const exactName = name === query || cityState === query;
    const exactZip = market.zipCodes.includes(rawQuery);
    const exact = exactName || exactZip;
    const starts = name.startsWith(query) || cityState.startsWith(query) || market.zipCodes.some(zip => zip.startsWith(query));
    const anchorBoost = anchorMarket?.nearbyMarkets.some(nearby => normalizeAutocomplete(nearby) === name) || normalizeAutocomplete(anchorTown) === name ? 8 : 0;
    const baseScore = exactName ? 110 : exactZip ? 97 : starts ? 96 : 78;

    add({
      label: `${market.displayName} homes`,
      query: `${market.displayName}, ${state || "NJ"}`,
      detail: `${market.county}, ${market.state}`,
      category: "town",
      source,
    }, baseScore + anchorBoost);

    for (const zip of market.zipCodes) {
      if (zip.startsWith(query) || name.includes(query)) {
        add({
          label: `${zip} homes`,
          query: zip,
          detail: `${market.displayName}, ${market.state}`,
          category: "zip",
          source,
        }, (zip === rawQuery ? 116 : zip.startsWith(query) ? 104 : name === query ? 94 : 76) + anchorBoost);
      }
    }

    for (const search of market.suggestedSearches.slice(0, exact ? 4 : 2)) {
      const lowerSearch = normalizeAutocomplete(search);
      const score = lowerSearch.includes(query) ? 88 : 68;
      add({
        label: search,
        query: normalizeSearchQueryForSubmission(search),
        detail: "Search idea",
        category: "suggested",
        source,
      }, score + anchorBoost);
    }

    for (const school of market.schoolSuggestions || []) {
      if (normalizeAutocomplete(school).includes(query) || name.includes(query)) {
        add({
          label: school,
          query: `${school}, NJ homes`,
          detail: "School or district search",
          category: "school",
          source,
        }, 72 + anchorBoost);
      }
    }

    // A neighbouring town is listed only when it matches what was typed too.
    for (const nearby of market.nearbyMarkets.slice(0, 4).filter(town => normalizeAutocomplete(town).includes(query))) {
      add({
        label: `Nearby: ${nearby}`,
        query: `${nearby}, NJ`,
        detail: `Near ${market.displayName}`,
        category: "nearby",
        source,
      }, (exact ? 89 : 58) + anchorBoost);
    }
  }

  for (const market of availableMarketMatches) {
    const city = market.city.trim();
    const state = normalizeState(market.state);
    if (!city || !state) continue;
    const name = normalizeAutocomplete(city);
    const cityState = normalizeAutocomplete(`${city} ${state}`);
    const exact = name === query || cityState === query;
    const starts = name.startsWith(query) || cityState.startsWith(query);
    add({
      label: `${city} homes`,
      query: `${city}, ${state}`,
      detail: `OpenHomesAI market, ${state}`,
      category: "town",
      source,
    }, exact ? 108 : starts ? 92 : 70);
  }

  if (!suggestions.length && anchorMarket) {
    for (const nearby of anchorMarket.nearbyMarkets.slice(0, 4)) {
      // Towns near a recent search appear only when they match the typed text;
      // they used to fill the list for anything typed, a typo included.
      if (normalizeAutocomplete(nearby).includes(query)) {
        add({
          label: `${nearby} homes`,
          query: `${nearby}, NJ`,
          detail: `Near your recent ${anchorMarket.displayName} search`,
          category: "nearby",
          source,
        }, normalizeAutocomplete(nearby).includes(query) ? 70 : 45);
      }
    }
  }

  // The backend's matching (services/town_match.py): every synced town, typos
  // tolerated. A near-miss the text resolves to leads the list as a question; the
  // equally close towns of an ambiguous one each get a row; exact and prefix towns
  // join the town rows.
  const townMatches = input.townMatches && normalizeAutocomplete(input.townMatches.query) === query ? input.townMatches : null;
  const resolution = townMatches?.resolution;
  const confidentTown = resolution?.status === "corrected" || resolution?.status === "ambiguous";
  if (resolution?.status === "corrected") {
    add({
      label: `Did you mean ${resolution.town}, ${resolution.state}?`,
      query: `${resolution.town}, ${resolution.state}`,
      detail: "The closest town to what you typed",
      category: "didyoumean",
      source,
    }, 130);
  } else if (resolution?.status === "ambiguous") {
    resolution.options.forEach((option, index) => {
      add({
        label: `Did you mean ${option.town}, ${option.state}?`,
        query: `${option.town}, ${option.state}`,
        detail: "One of the towns closest to what you typed",
        category: "didyoumean",
        source,
      }, 126 - index);
    });
  }
  // In the backend's order (exact, then the shortest prefix match first), so "morr"
  // leads with Morristown rather than an alphabetical Morris Plains.
  (townMatches?.matches || []).forEach((match, index) => {
    if (match.kind === "fuzzy") return;
    add({
      label: `${match.town} homes`,
      query: `${match.town}, ${match.state}`,
      detail: `${match.town}, ${match.state}`,
      category: "town",
      source,
    }, (match.kind === "exact" ? 112 : 97) - index / 100);
  });

  const typedMatchesExistingSuggestion = suggestions.some(item => {
    const itemQuery = normalizeAutocomplete(item.query);
    const itemLabel = normalizeAutocomplete(item.label.replace(/^Nearby:\s*/i, "").replace(/\s+homes$/i, ""));
    return itemQuery === query || itemLabel === query;
  });
  if (rawQuery.length >= 3 && !typedMatchesExistingSuggestion) {
    add({
      label: `Search ${rawQuery}`,
      query: rawQuery,
      detail: "Use exactly what you typed",
      category: "custom",
      source,
    }, (matchedMarkets.length || availableMarketMatches.length || confidentTown || (townMatches?.matches.length ?? 0) > 0) ? 64 : 108);
  }

  // Nothing matched what was typed: the list holds only the typed search itself,
  // never default towns that have nothing to do with it.
  return dedupeAutocomplete(suggestions);
}

export function getHomepageSearchRecommendations({
  user,
  recentSearches = [],
  savedSearches = [],
  favorites = [],
  browserLocation = null,
  approximateRegion = null,
  marketConfig = MARKET_RECOMMENDATIONS,
}: RecommendationInput): HomepageSearchRecommendation[] {
  const recent = recentSearches.find(item => cityFromContext(item, marketConfig) || item.query);
  const recentTown = cityFromContext(recent, marketConfig);
  if (user && recent && recentTown) {
    const key = getMarketKeyForTown(recentTown, marketConfig);
    const market = key ? marketConfig[key] : null;
    if (market) return recommendationForRecentTown(recent, toTitleTown(recentTown), market);
  }

  const saved = savedSearches.find(item => cityFromContext(item, marketConfig));
  const savedTown = cityFromContext(saved, marketConfig);
  if (user && saved && savedTown) {
    const key = getMarketKeyForTown(savedTown, marketConfig);
    const market = key ? marketConfig[key] : null;
    if (market) return recommendationForRecentTown(saved, toTitleTown(savedTown), market).map(item => ({ ...item, source: "saved_search", reason: `Based on your saved ${savedTown} search` }));
  }

  const favoriteTown = favorites.map(item => item.city).find(Boolean);
  if (user && favoriteTown) {
    const key = getMarketKeyForTown(favoriteTown, marketConfig);
    const market = key ? marketConfig[key] : null;
    if (market) return market.starterSearches.slice(0, 4).map(search => starterToRecommendation(search, "profile", `Based on homes you saved near ${favoriteTown}`));
  }

  const browserKey = getMarketKeyForCoordinates(browserLocation);
  if (browserKey) {
    return marketConfig[browserKey].starterSearches.slice(0, 4).map(search => starterToRecommendation(search, "browser_location", `Popular searches near ${marketConfig[browserKey].label}`));
  }

  const approximateKey = getMarketKeyForApproximateRegion(approximateRegion);
  if (approximateKey) {
    return marketConfig[approximateKey].starterSearches.slice(0, 4).map(search => starterToRecommendation(search, "approximate_region", `Search ideas for ${marketConfig[approximateKey].label}`));
  }

  return DEFAULT_ORDER.flatMap(key => marketConfig[key].starterSearches.slice(0, 1))
    .slice(0, 4)
    .map(search => starterToRecommendation(search, "fallback", "Popular starter search"));
}

export function getHomepageMarketOrder(input: RecommendationInput): string[] {
  const marketConfig = input.marketConfig || MARKET_RECOMMENDATIONS;
  const recentTown = cityFromContext(input.recentSearches?.[0], marketConfig);
  const savedTown = cityFromContext(input.savedSearches?.[0], marketConfig);
  const favoriteTown = input.favorites?.map(item => item.city).find(Boolean) || null;
  const preferred =
    getMarketKeyForTown(recentTown, marketConfig) ||
    getMarketKeyForTown(savedTown, marketConfig) ||
    getMarketKeyForTown(favoriteTown, marketConfig) ||
    getMarketKeyForCoordinates(input.browserLocation) ||
    getMarketKeyForApproximateRegion(input.approximateRegion);
  const allKeys = Object.keys(marketConfig);
  return preferred ? [preferred, ...allKeys.filter(key => key !== preferred)] : DEFAULT_ORDER.filter(key => key in marketConfig);
}

export function getRecommendationHeading(recommendations: HomepageSearchRecommendation[]) {
  const source = recommendations[0]?.source;
  if (source === "recent_search" || source === "saved_search" || source === "profile") return "Based on your recent searches";
  if (source === "browser_location") return "Starter searches near you";
  if (source === "approximate_region") return "Search ideas for your area";
  return "Popular starter searches";
}
